/** @fileoverview Hook that runs an action from a button, tracks its busy and error state, and reloads the rows and lists it changes. */

import { useEffect, useRef, useState } from 'react';

import { isRuntimeFile, saveRuntimeFile } from '../../services/runtimeResponse';

import type {
  FabrikRecordKeyRefValue,
  FabrikStateComponent,
  InputSnapshot,
} from '../../types/apps';
import { useApplyWrites, useComponentState, useReloadData } from './appRuntimeContext';
import { useComponentStateStore } from './componentStateStore';
import type { FieldValues } from './types';
import { useComponentStateRefresh } from './useComponentStateRows';
import { actionHasStep, useActionToast, writeFailureText } from './useActionToast';

/** How long the done tick stays on the button after a run that worked. */
const DONE_DURATION_MS = 2000;

/** A checklist or timeline an action writes to, with the refs that identify its record. */
type ActionTarget = Pick<FabrikStateComponent, 'id' | 'recordKey'>;

/** What one run of an action sends, and the components it reloads afterwards. */
type RunRequest = {
  actionId: string;
  screenId: string;
  fields: FieldValues;
  inputs: InputSnapshot;
  targets: ActionTarget[];
  recordKeyRefs?: FabrikRecordKeyRefValue[];
  item?: { componentId: string; key: string };
};

/**
 * Runs one action for the record on screen. Keeps the button busy until the written rows have
 * reloaded, so a showWhen button cannot run the action twice, and shows a toast or the error.
 * After a run that worked, `isDone` stays true for a moment, so the button can show a tick.
 * When the server answers with a file (a download export), saves it in the browser. An action
 * with a confirm question waits until the viewer accepts it in `confirmation`'s dialog.
 */
export const useRunAction = () => {
  const componentState = useComponentState();
  const { actions } = useComponentStateStore();
  const [waitingRun, setWaitingRun] = useState<RunRequest | null>(null);
  const { refreshAfterAction } = useComponentStateRefresh();
  const reloadData = useReloadData();
  const applyWrites = useApplyWrites();
  const { showActionToast } = useActionToast();
  const [isBusy, setIsBusy] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState('');
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (doneTimerRef.current) {
        clearTimeout(doneTimerRef.current);
      }
    },
    []
  );

  /** Shows the done tick, then hides it after a moment. */
  const showDone = () => {
    if (doneTimerRef.current) {
      clearTimeout(doneTimerRef.current);
    }
    setIsDone(true);
    doneTimerRef.current = setTimeout(() => setIsDone(false), DONE_DURATION_MS);
  };

  /**
   * Runs the action and shows a short message when it fails. Sends the whole record, not only
   * the key: the action's rows and emails read other record fields, such as the manager's email.
   * An action that writes to a system gets a new run key on each press, and every list on the
   * screen reads its data again after it runs. A write that did not work shows as the error.
   * @param request the action id, the record fields, the components it writes to, and the
   *   checklist row when run from a row button
   */
  const runNow = async ({
    actionId,
    screenId,
    fields,
    inputs,
    targets,
    recordKeyRefs = [],
    item,
  }: RunRequest) => {
    if (!componentState) {
      return;
    }
    const writes = actionHasStep(actions, actionId, 'write');
    setIsBusy(true);
    setIsDone(false);
    setError('');
    let hasWorked = false;
    try {
      const result = await componentState.runAction({
        actionId,
        screenId,
        fields,
        inputs,
        recordKey: [...targets.flatMap((target) => target.recordKey), ...recordKeyRefs],
        item,
        runKey: writes ? crypto.randomUUID() : undefined,
      });
      const failureText = isRuntimeFile(result)
        ? ''
        : [writeFailureText(result.writes), result.partial?.message].filter(Boolean).join(' ');
      if (isRuntimeFile(result)) {
        saveRuntimeFile(result);
      } else {
        /* A run that failed in part shows only the error, never a success toast. */
        if (!failureText) {
          showActionToast(actionId, result);
        }
        /* The record on screen shows what was saved, as the lists read theirs again. */
        if (result.writes) {
          applyWrites(result.writes);
        }
      }
      setError(failureText);
      hasWorked = !failureText;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to run this action.');
    } finally {
      /*
       * Refresh after a failure too: the server can still store rows or write records for this
       * action when it threw or failed part way, for example when only the email step failed.
       */
      if (writes) {
        reloadData();
      }
      await refreshAfterAction(targets, fields, screenId, inputs);
      setIsBusy(false);
      /* The tick shows once the button is usable again, so the viewer sees it. */
      if (hasWorked) {
        showDone();
      }
    }
  };

  /**
   * Runs the action, or first asks its confirm question when it has one.
   * @param request what the run sends and reloads
   */
  const runAction = (request: RunRequest) => {
    const action = actions.find(({ id }) => id === request.actionId);
    if (action?.confirm) {
      setWaitingRun(request);
      return;
    }
    void runNow(request);
  };

  const confirmation = {
    confirm: waitingRun ? actions.find(({ id }) => id === waitingRun.actionId)?.confirm : undefined,
    onAccept: () => {
      if (waitingRun) {
        setWaitingRun(null);
        void runNow(waitingRun);
      }
    },
    onCancel: () => setWaitingRun(null),
  };

  return { canRun: Boolean(componentState), isBusy, isDone, error, runAction, confirmation };
};
